<template>
  <v-navigation-drawer
    v-model="drawer"
    app
    fixed
    width="330"
    stateless
    :mini-variant.sync="minDrawer"
    mini-variant-width="110"
  >
    <navigation-menu-admin-info :min-drawer.sync="minDrawer" />

    <v-list
      dense
      subheader
    >
      <template v-for="(menuItem, key) in menuItems">
        <div
          v-if="menuItem.visible"
          :key="key"
        >
          <v-subheader
            class="ml-0"
            inset
          >{{ menuItem.title }}</v-subheader>

          <template v-for="subItem in menuItem.subItems">
            <template v-if="subItem.subItems">
              <v-list-group
                v-if="subItem.visible"
                :key="subItem.id"
                :value="false"
              >

                <v-list-tile
                  :id="subItem.id"
                  slot="activator"
                >
                  <v-list-tile-action>
                    <v-icon>{{ subItem.icon }}</v-icon>
                  </v-list-tile-action>

                  <v-list-tile-content class="subheading">
                    <v-list-tile-title>{{ subItem.title }}</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>

                <navigation-menu-item
                  v-for="innerSubItem in subItem.subItems"
                  :key="innerSubItem.id"
                  :menu-item="innerSubItem"
                />
              </v-list-group>
            </template>

            <navigation-menu-item
              v-else
              :key="subItem.id"
              :menu-item="subItem"
            />
          </template>

          <v-divider class="grey lighten-3 mt-3" />
        </div>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { AppConfig } from '@/config/config'

  // Utils
  import { createClientRequestListRoute } from '@/utils/routing/ClientRequests'
  import { createForexSetupRatesRoute, createForexRequestListRoute } from '@/utils/routing/Forex'
  import { createUserListRoute } from '@/utils/routing/Users'
  import { createCardOrderListRoute, createCardListRoute } from '@/utils/routing/Issuing'
  import { createSwiftMessageListRoute } from '@/utils/routing/SwiftMessages'
  import { createSwiftMessageOrderListRoute } from '@/utils/routing/SwiftMessageOrders'
  import { createPaymentOrderListRoute } from '@/utils/routing/PaymentOrders'
  import { createTransactionalFeeListRoute } from '@/utils/routing/Fees'
  import { createFeeGroupListRoute } from '@/utils/routing/FeeGroups'
  import { createReferralListRoute } from '@/utils/routing/Referrals'
  import { createReferralCampaignsListRoute } from '@/utils/routing/ReferralCampaigns'
  import { createBannerListRoute } from '@/utils/routing/Banners'
  import { createTarget2ParticipantListRoute } from '@/utils/routing/Target2Participants'
  import { createCzcbSwiftListRoute } from '@/utils/routing/CzcbSwifts'
  import { createLegalCashbackSetupRoute } from '@/utils/routing/Cashback'
  import { createDepositsSetupRoute } from '@/utils/routing/Deposits'
  import { createCustomRequestListRoute } from '@/utils/routing/CustomRequests'
  import { createCountryGroupListRoute } from '@/utils/routing/CountryGroups'
  import { createRateListRoute } from '@/utils/routing/Rates'
  import { createBolScreenshotsRoute } from '@/utils/routing/BolScreenshots'
  import { createNotificationCreateRoute } from '@/utils/routing/Notifications'
  import { createFinChannelListRoute } from '@/utils/routing/FinChannels'
  import { createWorkingDaysCalendarRoute } from '@/utils/routing/WorkingDays'
  import {
    createPrivateProfileListRoute,
    createLegalProfileListRoute,
    createRemittanceProfileListRoute
  } from '@/utils/routing/Profiles'
  import {
    createPaymentListRoute,
    createOnHoldPaymentListRoute,
    createIntegratorOnHoldPaymentListRoute,
    createUnassignedPaymentListRoute,
    createVerificationPaymentListRoute,
    createUndefinedTopupListRoute,
    createNotSettledPaymentListRoute,
    createPendingCancellationPaymentListRoute,
    createErrorPaymentListRoute,
    createPendingPaymentListRoute,
    createPostponedSwiftPaymentListRoute
  } from '@/utils/routing/Payments'
  import { createChannelRoutesCurrencyListRoute } from '@/utils/routing/ChannelSetup'
  import { createSubscriptionSetupRoute } from '@/utils/routing/Subscription'

  // Constants
  import { AUTHORITIES, BASE_ACCOUNT_ROLES, ROLES } from '@/store/admin'
  import { FEATURES } from '@/store/features'
  import { PROFILE_TYPES } from '@/models/AccountProfile'
  import { COUNTRY_GROUP_CATEGORIES } from '@/store/countries'

  // Mixins
  import modal from '@/mixins/modal'

  // Components
  import NavigationMenuAdminInfo from '@/components/layout/NavigationMenuAdminInfo'
  import NavigationMenuItem from '@/components/layout/NavigationMenuItem'
  import { createObApplicationsRoute, createObPaymentsRoute, createObInstitutionsRoute } from '@/utils/routing/OpenBanking'

  export default {
    name: 'NavigationMenu',
    components: {
      NavigationMenuAdminInfo,
      NavigationMenuItem
    },
    mixins: [modal],
    data () {
      return {
        AUTHORITIES,
        drawer: true,
        minDrawer: false
      }
    },
    computed: {
      ...mapGetters('admin', ['hasAuthorityReadPermission', 'hasRole']),
      ...mapGetters('features', ['isFeatureEnabled']),
      ...mapGetters('payments', [
        'onHoldPaymentTotalCount',
        'onHoldIntegratorPaymentTotalCount',
        'unassignedPaymentTotalCount',
        'verificationPaymentTotalCount',
        'identifiedRecipientVerificationPaymentTotalCount',
        'pendingPaymentTotalCount',
        'processingPaymentTotalCount',
        'pendingCancelationPaymentTotalCount',
        'errorPaymentTotalCount',
        'postponedSwiftPaymentTotalCount'
      ]),
      ...mapGetters('paymentOrders', ['paymentOrderTotalCount']),
      ...mapGetters('swiftMessageOrders', ['swiftMessageOrderTotalCount']),
      ...mapGetters('clientRequests', ['unreadClientRequestTotalCount', 'unreadForexTotalCount']),
      ...mapGetters('cardOrders', ['cardOrderTotalCount']),
      firstCountryGroupCategory () {
        return Object.keys(COUNTRY_GROUP_CATEGORIES)[0]
      },
      menuItems () {
        return [{
          title: 'Referrals',
          visible: this.hasRole([ROLES.REFERRAL, ROLES.REFERRAL_PROGRAM_READ, ROLES.REFERRAL_PROGRAM_WRITE]),
          subItems: [{
            id: 'referrals',
            icon: 'group',
            title: 'Referral list',
            route: createReferralListRoute(),
            query: {},
            visible: this.hasRole([ROLES.REFERRAL])
          }, {
            id: 'campaigns',
            icon: 'hotel_class',
            title: 'Campaigns',
            route: createReferralCampaignsListRoute(),
            query: {},
            visible: this.hasRole([ROLES.REFERRAL_PROGRAM_READ, ROLES.REFERRAL_PROGRAM_WRITE])
          }]
        }, {
          title: 'Users',
          visible: this.hasRole(BASE_ACCOUNT_ROLES),
          subItems: [
            {
              id: 'user-list',
              icon: 'group',
              title: 'User list',
              route: createUserListRoute(),
              visible: true
          }]
        }, {
          title: 'Profiles',
          visible: this.hasRole([...BASE_ACCOUNT_ROLES, ROLES.FRAUD_MONITORING_READ, ROLES.FRAUD_MONITORING_WRITE]),
          subItems: [{
            id: 'private-profiles',
            icon: 'person',
            title: 'Private',
            route: createPrivateProfileListRoute(),
            visible: true
          }, {
            id: 'legal-profiles',
            icon: 'work',
            title: 'Legal',
            route: createLegalProfileListRoute(),
            visible: true
          }, {
            id: 'remittance-profiles',
            icon: 'person_outline',
            title: 'Remittance',
            route: createRemittanceProfileListRoute(),
            visible: this.hasRole(BASE_ACCOUNT_ROLES)
          }, {
            id: 'client-requests',
            icon: 'chat',
            title: 'Client requests',
            route: createClientRequestListRoute(),
            query: {},
            counter: this.unreadClientRequestTotalCount,
            visible: this.hasRole([
              ROLES.ACCOUNTANT_READ, ROLES.ACCOUNTANT_WRITE,
              ROLES.SUPPORT_READ, ROLES.SUPPORT_WRITE,
              ROLES.MANAGER, ROLES.MANAGER_LEAD
            ])
          }]
        }, {
          title: 'Forex',
          visible: this.hasRole(ROLES.ACCOUNTANT_WRITE),
          subItems: [{
            id: 'forex-setup',
            icon: 'tune',
            title: 'Forex setup',
            route: createForexSetupRatesRoute(),
            query: {},
            visible: true
          }, {
            id: 'exchange-requests',
            icon: 'currency_exchange',
            title: 'Exchange requests',
            route: createForexRequestListRoute(),
            query: {},
            counter: this.unreadForexTotalCount,
            visible: true
          }]
        }, {
          title: 'Payments',
          visible: this.hasRole(BASE_ACCOUNT_ROLES),
          subItems: [{
            id: 'payment-reports',
            icon: 'archive',
            title: 'Reports',
            visible: this.hasRole([
              ROLES.MANAGER,
              ROLES.MANAGER_LEAD,
              ROLES.AML_READ,
              ROLES.AML_WRITE,
              ROLES.AML_SUPERVISOR,
              ROLES.ACCOUNTANT_READ,
              ROLES.ACCOUNTANT_WRITE,
              ROLES.ACCOUNTANT_SUPERVISOR
            ]),
            subItems: [
              {
                id: 'metabase-reports',
                title: 'Metabase reports',
                href: AppConfig.REPORTS_URL,
                visible: this.hasRole([
                  ROLES.MANAGER,
                  ROLES.MANAGER_LEAD,
                  ROLES.AML_READ,
                  ROLES.AML_WRITE,
                  ROLES.AML_SUPERVISOR,
                  ROLES.ACCOUNTANT_READ,
                  ROLES.ACCOUNTANT_WRITE,
                  ROLES.ACCOUNTANT_SUPERVISOR
                ])
              }
            ]
          }, {
            id: 'payments',
            icon: 'functions',
            title: 'All',
            route: createPaymentListRoute(),
            query: {},
            visible: true
          }, {
            id: 'payments-on-hold',
            icon: 'event_available',
            title: 'On hold',
            route: createOnHoldPaymentListRoute(),
            query: {},
            counter: this.onHoldPaymentTotalCount,
            visible: true
          }, {
            id: 'payments-on-hold-integrator',
            icon: 'event_note',
            title: 'On hold integrator',
            route: createIntegratorOnHoldPaymentListRoute(),
            query: {},
            counter: this.onHoldIntegratorPaymentTotalCount,
            visible: true
          }, {
            title: 'Open Banking',
            icon: 'account_balance',
            visible: this.hasRole(BASE_ACCOUNT_ROLES),
            id: 'open-banking',
            subItems: [{
              id: 'ob-institutions',
              title: 'Open banking institutions',
              route: createObInstitutionsRoute(),
              visible: this.hasRole([ROLES.SUPPORT_WRITE, ROLES.SUPPORT_READ])
            }, {
              id: 'applications',
              title: 'Applications',
              route: createObApplicationsRoute(),
              visible: true
            }, {
              id: 'open-banking-payments',
              title: 'Open Banking payments',
              route: createObPaymentsRoute(),
              visible: true
            }]
          }, {
            id: 'unassigned-payments',
            icon: 'edit',
            title: 'Unassigned',
            route: createUnassignedPaymentListRoute(),
            query: {},
            counter: this.unassignedPaymentTotalCount,
            counterClasses: 'red lighten-1 white--text',
            visible: true
          }, {
            id: 'payment-orders',
            icon: 'vertical_align_bottom',
            title: '2nd approval',
            route: createPaymentOrderListRoute(),
            query: {},
            counter: this.paymentOrderTotalCount,
            visible: true
          }, {
            id: 'pending-payments',
            icon: 'hourglass_empty',
            title: 'Pending',
            route: createPendingPaymentListRoute(),
            query: {},
            counter: this.pendingPaymentTotalCount,
            visible: true
          }, {
            id: 'verification-payments',
            icon: 'done_all',
            title: 'Verification',
            route: createVerificationPaymentListRoute(),
            query: {},
            counter: this.verificationPaymentTotalCount,
            visible: true
          }, {
            id: 'unidentified-topup-payments',
            icon: 'help_outline',
            title: 'Unidentified topups',
            route: createUndefinedTopupListRoute(),
            query: {},
            counter: this.identifiedRecipientVerificationPaymentTotalCount,
            visible: true
          }, {
            id: 'processing-payments',
            icon: 'access_time',
            title: 'Not settled',
            route: createNotSettledPaymentListRoute(),
            query: {},
            counter: this.processingPaymentTotalCount,
            visible: true
          }, {
            id: 'cancellation-payments',
            icon: 'remove_circle_outline',
            title: 'Cancellation',
            route: createPendingCancellationPaymentListRoute(),
            query: {},
            counter: this.pendingCancelationPaymentTotalCount,
            visible: true
          }, {
            id: 'error-payments',
            icon: 'error_outline',
            title: 'Errors',
            route: createErrorPaymentListRoute(),
            query: {},
            counter: this.errorPaymentTotalCount,
            visible: true
          }]
        }, {
          title: 'SWIFT messages',
          visible: this.hasRole([ROLES.ACCOUNTANT_READ, ROLES.ACCOUNTANT_WRITE, ROLES.ACCOUNTANT_SWIFT]),
          subItems: [{
            id: 'swift-messages',
            icon: 'route',
            title: 'All',
            route: createSwiftMessageListRoute(),
            query: {},
            visible: true
          }, {
            id: 'swift-message-orders',
            icon: 'vertical_align_bottom',
            title: '2nd approval',
            route: createSwiftMessageOrderListRoute(),
            query: {},
            counter: this.swiftMessageOrderTotalCount,
            visible: true
          }, {
            id: 'postponed-swift-payments',
            icon: 'hourglass_bottom',
            title: 'Payment queue',
            route: createPostponedSwiftPaymentListRoute(),
            query: {},
            counter: this.postponedSwiftPaymentTotalCount,
            visible: this.hasRole([ROLES.ACCOUNTANT_SWIFT])
          }, {
            id: 'fin-channels',
            icon: 'list',
            title: 'Fin channels',
            route: createFinChannelListRoute(),
            query: {},
            visible: this.hasRole(ROLES.ACCOUNTANT_SWIFT)
          }]
        }, {
          title: 'Cards',
          visible: this.isFeatureEnabled(FEATURES.CARD_ISSUING) && this.hasRole(BASE_ACCOUNT_ROLES),
          subItems: [{
            id: 'card-orders',
            icon: 'vertical_align_bottom',
            title: 'Orders',
            route: createCardOrderListRoute(),
            query: {},
            counter: this.cardOrderTotalCount,
            visible: true
          }, {
            id: 'cards',
            icon: 'credit_card',
            title: 'Cards',
            route: createCardListRoute(),
            query: {},
            visible: true
          }]
        }, {
          title: 'Fees',
          visible: this.hasRole([
            ROLES.ACCOUNTANT_WRITE, ROLES.ACCOUNTANT_READ,
            ROLES.AML_WRITE, ROLES.AML_READ,
            ROLES.MANAGER_LEAD, ROLES.MANAGER
          ]),
          subItems: [{
            id: 'transaction-fees',
            icon: 'local_atm',
            title: 'Fees',
            route: createTransactionalFeeListRoute(),
            query: {},
            visible: true
          }, {
            id: 'fee-groups',
            icon: 'share',
            title: 'Fee groups',
            route: createFeeGroupListRoute(),
            query: {},
            visible: true
          }]
        }, {
          title: 'System',
          visible: this.hasRole([...BASE_ACCOUNT_ROLES, ROLES.MARKETING]),
          subItems: [{
            id: 'rates',
            icon: 'multiline_chart',
            title: 'Rates',
            route: createRateListRoute(),
            query: {},
            visible: this.hasRole(BASE_ACCOUNT_ROLES)
          }, {
            id: 'cashback',
            icon: '360',
            title: 'Cashback',
            route: createLegalCashbackSetupRoute(),
            query: {},
            visible: this.hasRole([ROLES.SUPPORT_READ, ROLES.SUPPORT_WRITE])
          }, {
            id: 'savings',
            icon: 'paid',
            title: 'Savings',
            route: createDepositsSetupRoute(),
            query: {},
            visible: this.hasRole([ROLES.SUPPORT_READ, ROLES.SUPPORT_WRITE])
          }, {
            id: 'subscription',
            icon: 'fact_check',
            title: 'Subscription plans',
            route: createSubscriptionSetupRoute(),
            query: {},
            visible: this.hasRole([ROLES.FEE_MANAGER])
          }, {
            id: 'calendar',
            icon: 'event',
            title: 'Working days',
            route: createWorkingDaysCalendarRoute(),
            query: {},
            visible: this.hasRole(BASE_ACCOUNT_ROLES)
          }, {
            id: 'target2-participants',
            icon: 'list',
            title: 'TARGET2 participants',
            route: createTarget2ParticipantListRoute(),
            query: {},
            visible: this.hasRole(BASE_ACCOUNT_ROLES)
          }, {
            id: 'czcb-participants',
            icon: 'list',
            title: 'CZCB swifts',
            route: createCzcbSwiftListRoute(),
            query: {},
            visible: this.hasRole(BASE_ACCOUNT_ROLES)
          }, {
            id: 'channel-setup',
            icon: 'move_down',
            title: 'Channel routes',
            route: createChannelRoutesCurrencyListRoute(),
            query: {},
            visible: this.hasRole([ROLES.CHANNEL_READ, ROLES.CHANNEL_WRITE])
          }, {
            id: 'bol-screenshots',
            icon: 'screenshot_monitor',
            title: 'BoL screenshots',
            route: createBolScreenshotsRoute(),
            query: {},
            visible: this.hasRole([ROLES.ACCOUNTANT_READ, ROLES.ACCOUNTANT_WRITE])
          }, {
            id: 'notifications',
            icon: 'message',
            title: 'Notifications',
            route: createNotificationCreateRoute(),
            query: {},
            visible: this.hasRole(ROLES.MARKETING)
          }, {
            id: 'banners',
            icon: 'view_agenda',
            title: 'Banners',
            route: createBannerListRoute({ query: { profileType: PROFILE_TYPES.LEGAL, license: 'GB' } }),
            visible: this.hasRole(ROLES.MARKETING)
          }, {
            id: 'custom-queries',
            icon: 'auto_fix_high',
            title: 'Custom requests',
            route: createCustomRequestListRoute(),
            visible: this.hasRole([ROLES.ACCOUNTANT_WRITE, ROLES.ACCOUNTANT_SUPERVISOR])
          }, {
            id: 'country-groups',
            icon: 'language',
            title: 'Country groups',
            route: createCountryGroupListRoute(this.firstCountryGroupCategory),
            visible: this.hasRole([ROLES.COUNTRY_GROUPS_MANAGER])
          }]
        }]
      }
    }
  }
</script>

<style lang="scss">
  .v-navigation-drawer--mini-variant {
    .v-chip {
      opacity: 0;
    }

    .v-subheader,
    .v-divider {
      display: flex !important;
    }

    .navigation-toggle {
      display: none;
    }
  }

  .v-navigation-drawer {
    .v-subheader {
      margin-top: 13px;
    }

    .v-subheader,
    .v-list__tile {
      padding-right: 30px;
      padding-left: 30px;
    }

    &.v-navigation-drawer--mini-variant {
      .v-subheader {
        justify-content: center;
        padding-right: 0;
        padding-left: 0;
      }
    }

    .v-list__tile--active {
      > .v-list__tile--link {
        color: #d32f2f;
        caret-color: #d32f2f
      }
    }
  }
</style>
